<script lang="ts">
  import type { Snippet } from "svelte"
  import type { PopoverMenuItem } from "@kontextwerk/web-sdk/ui/overlay/popover/types"
  import { newApproval } from "@kontextwerk/web-sdk/core/overlay/approvals"
  import { newNotification } from "@kontextwerk/web-sdk/core/overlay/notifications"
  import { mdiArchiveOutline, mdiArchiveArrowUpOutline } from "@mdi/js"
  import {
    archiveCustomersBatch,
    restoreCustomersBatch,
  } from "../../../../../../../../../../api/kehrblatt/customer-registry/customers"
  import {
    registryArchiveIssueMessage,
    registryArchiveProcessed,
  } from "../../../../../../../../_shared/_logic/batch-results"

  let {
    customer,
    disabled = false,
    onChanged,
    children,
  }: {
    customer: Customer | null
    disabled?: boolean
    onChanged: (customer: Customer) => void
    children: Snippet<[PopoverMenuItem[]]>
  } = $props()

  let saving = $state(false)
  let confirming = $state(false)
  const archived = $derived(Boolean(customer?.archived_at))

  const perform = async (target: Customer) => {
    if (
      saving ||
      disabled ||
      customer?.id !== target.id ||
      customer?.organization_id !== target.organization_id
    )
      return

    saving = true
    try {
      if (target.archived_at) {
        const response = await restoreCustomersBatch([target.id])

        if (!registryArchiveProcessed(response, target.id)) {
          newNotification({
            class: "warning",
            html: registryArchiveIssueMessage(
              response,
              target.id,
              "Ansprechpartner wurde nicht wiederhergestellt.",
            ),
          })

          return
        }

        if (customer?.id === target.id && customer?.organization_id === target.organization_id)
          onChanged({ ...customer, archived_at: null, archive_reason: null })

        newNotification({ class: "success", html: "Ansprechpartner wiederhergestellt." })
      } else {
        const response = await archiveCustomersBatch([target.id])

        if (!registryArchiveProcessed(response, target.id)) {
          newNotification({
            class: "warning",
            html: registryArchiveIssueMessage(
              response,
              target.id,
              "Ansprechpartner wurde nicht archiviert.",
            ),
          })

          return
        }

        if (customer?.id === target.id && customer?.organization_id === target.organization_id)
          onChanged({
            ...customer,
            archived_at: new Date().toISOString(),
            archive_reason: "USER_REQUEST",
          })

        newNotification({ class: "success", html: "Ansprechpartner archiviert." })
      }
    } catch (err) {
      console.error(`customer_${archived ? "restore" : "archive"}_failed`, err)
      newNotification({
        class: "error",
        html: `Ansprechpartner konnte nicht ${archived ? "wiederhergestellt" : "archiviert"} werden.`,
      })
    } finally {
      saving = false
    }
  }

  const mutate = async () => {
    if (!customer || saving || disabled || confirming) return

    const target = customer

    if (archived) return perform(target)

    let submitted = false
    confirming = true
    try {
      await newApproval({
        title: "Ansprechpartner archivieren",
        description:
          "Diesen Ansprechpartner aus neuen Suchen und Zuordnungen ausblenden? Bestehende Aufträge, Kontaktpunkte und Zuordnungen bleiben erhalten.",
        approvalLabel: "Ja, archivieren",
        declineLabel: "Abbrechen",
        approvalCallback: async () => {
          if (submitted) return

          submitted = true
          await perform(target)
        },
      })
    } finally {
      confirming = false
    }
  }
  const actions = $derived<PopoverMenuItem[]>(
    customer
      ? [
          {
            id: archived ? "restore" : "archive",
            label: archived ? "Wiederherstellen" : "Archivieren",
            icon: archived ? mdiArchiveArrowUpOutline : mdiArchiveOutline,
            disabled: disabled || saving || confirming,
            tone: archived ? "default" : "danger",
            onClick: mutate,
          },
        ]
      : [],
  )
</script>

{@render children(actions)}
